/**
 * Performance Monitor
 * 
 * Centralized performance monitoring and metrics collection
 */

import { PerformanceMetrics, MemoryUsage } from '../types';

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: PerformanceMetrics[] = [];
  private readonly maxMetrics = 1000;
  private readonly storageKey = 'syntaxlens_performance_metrics';

  constructor() {
    this.startMemoryMonitoring();
  }

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Start timing an operation
   */
  startTimer(component: string, operation: string): string {
    const timerKey = `${component}_${operation}_${Date.now()}`;
    performance.mark(`${timerKey}_start`);
    return timerKey;
  }

  /**
   * End timing and record metric
   */
  endTimer(
    timerKey: string, 
    component: string, 
    operation: string, 
    success: boolean = true, 
    error?: string,
    metadata?: Record<string, any>
  ): void {
    const endMark = `${timerKey}_end`;
    performance.mark(endMark);
    
    try {
      const startMark = `${timerKey}_start`;
      performance.measure(timerKey, startMark, endMark);
      
      const measure = performance.getEntriesByName(timerKey)[0];
      const duration = measure ? measure.duration : 0;

      this.recordMetric({
        timestamp: new Date().toISOString(),
        component,
        operation,
        duration,
        success,
        error,
        metadata: {
          ...metadata,
          memory: this.getCurrentMemoryUsage()
        }
      });

      // Clean up performance marks
      performance.clearMarks(startMark);
      performance.clearMarks(endMark);
      performance.clearMeasures(timerKey);
    } catch (e) {
      console.warn('Performance monitoring error:', e);
    }
  }

  /**
   * Record a metric manually
   */
  recordMetric(metric: PerformanceMetrics): void {
    this.metrics.push(metric);

    // Trim if exceeds max
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }

    // Console logging in dev
    if (import.meta.env.DEV) {
      const style = metric.success ? 'color: green' : 'color: red';
      console.log(
        `%c[PERF] ${metric.component}.${metric.operation}`,
        style,
        `${Math.round(metric.duration)}ms`,
        metric
      );
    }

    // Persist to localStorage in dev
    if (import.meta.env.DEV) {
      this.saveToStorage();
    }
  }

  /**
   * Get current memory usage
   */
  getCurrentMemoryUsage(): MemoryUsage | undefined {
    try {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        return {
          usedJSHeapSize: memory.usedJSHeapSize,
          totalJSHeapSize: memory.totalJSHeapSize,
          jsHeapSizeLimit: memory.jsHeapSizeLimit
        };
      }
    } catch (e) {
      // Silently fail if memory API not available
    }
    return undefined;
  }

  /**
   * Get metrics
   */
  getMetrics(): PerformanceMetrics[] {
    return [...this.metrics];
  }

  /**
   * Get aggregated statistics
   */
  getStats(): {
    totalOperations: number;
    averageDuration: number;
    successRate: number;
    slowestOperations: PerformanceMetrics[];
    byComponent: Record<string, { count: number; averageDuration: number; successRate: number }>;
    byOperation: Record<string, { count: number; averageDuration: number; successRate: number }>;
  } {
    if (this.metrics.length === 0) {
      return {
        totalOperations: 0,
        averageDuration: 0,
        successRate: 0,
        slowestOperations: [],
        byComponent: {},
        byOperation: {}
      };
    }

    const successfulMetrics = this.metrics.filter(m => m.success);
    const averageDuration = this.metrics.reduce((acc, m) => acc + m.duration, 0) / this.metrics.length;
    const successRate = successfulMetrics.length / this.metrics.length;
    
    const slowestOperations = [...this.metrics]
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10);

    // Aggregate by component
    const byComponent: Record<string, { count: number; averageDuration: number; successRate: number }> = {};
    const byOperation: Record<string, { count: number; averageDuration: number; successRate: number }> = {};

    this.metrics.forEach(metric => {
      // By component
      if (!byComponent[metric.component]) {
        byComponent[metric.component] = { count: 0, averageDuration: 0, successRate: 0 };
      }
      byComponent[metric.component].count++;
      
      // By operation  
      if (!byOperation[metric.operation]) {
        byOperation[metric.operation] = { count: 0, averageDuration: 0, successRate: 0 };
      }
      byOperation[metric.operation].count++;
    });

    // Calculate averages
    Object.keys(byComponent).forEach(component => {
      const componentMetrics = this.metrics.filter(m => m.component === component);
      byComponent[component].averageDuration = 
        componentMetrics.reduce((acc, m) => acc + m.duration, 0) / componentMetrics.length;
      byComponent[component].successRate = 
        componentMetrics.filter(m => m.success).length / componentMetrics.length;
    });

    Object.keys(byOperation).forEach(operation => {
      const operationMetrics = this.metrics.filter(m => m.operation === operation);
      byOperation[operation].averageDuration = 
        operationMetrics.reduce((acc, m) => acc + m.duration, 0) / operationMetrics.length;
      byOperation[operation].successRate = 
        operationMetrics.filter(m => m.success).length / operationMetrics.length;
    });

    return {
      totalOperations: this.metrics.length,
      averageDuration: Math.round(averageDuration),
      successRate,
      slowestOperations,
      byComponent,
      byOperation
    };
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = [];
    if (import.meta.env.DEV) {
      localStorage.removeItem(this.storageKey);
    }
  }

  /**
   * Export metrics
   */
  exportMetrics(): string {
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      stats: this.getStats(),
      metrics: this.metrics
    }, null, 2);
  }

  /**
   * Start automatic memory monitoring
   */
  private startMemoryMonitoring(): void {
    if (!import.meta.env.DEV) return;

    setInterval(() => {
      const memory = this.getCurrentMemoryUsage();
      if (memory) {
        this.recordMetric({
          timestamp: new Date().toISOString(),
          component: 'System',
          operation: 'MemoryUsage',
          duration: 0,
          success: true,
          metadata: { memory }
        });
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Save to localStorage
   */
  private saveToStorage(): void {
    try {
      // Only save recent metrics to avoid storage bloat
      const recentMetrics = this.metrics.slice(-100);
      localStorage.setItem(this.storageKey, JSON.stringify(recentMetrics));
    } catch (e) {
      console.warn('Failed to save performance metrics to localStorage:', e);
    }
  }
}

// Export singleton
export const performanceMonitor = PerformanceMonitor.getInstance();

// Convenience function for timing operations
export function timeOperation<T>(
  component: string,
  operation: string,
  fn: () => T | Promise<T>,
  metadata?: Record<string, any>
): Promise<T> {
  const timer = performanceMonitor.startTimer(component, operation);
  const startTime = Date.now();

  try {
    const result = fn();
    
    if (result instanceof Promise) {
      return result.then(
        (value) => {
          performanceMonitor.endTimer(timer, component, operation, true, undefined, metadata);
          return value;
        },
        (error) => {
          performanceMonitor.endTimer(timer, component, operation, false, error.message, metadata);
          throw error;
        }
      );
    } else {
      performanceMonitor.endTimer(timer, component, operation, true, undefined, metadata);
      return Promise.resolve(result);
    }
  } catch (error) {
    performanceMonitor.endTimer(timer, component, operation, false, error.message, metadata);
    throw error;
  }
}